import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Seo from "@/components/Seo";
import { Reveal, MaskedLine, FadeIn } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("it-IT", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/blog`)
      .then((r) => setPosts(r.data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="blog-page">
      <Seo
        title="Blog | Laure Hila — Viaggia in Grande"
        description="Pensieri, viaggi e crescita personale: il blog di Laure Hila. Storie autentiche di cambiamento, senza filtri."
      />

      <section className="pt-32 md:pt-44 pb-12 md:pb-16 grain relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeIn>
            <p className="eyebrow mb-5">Blog</p>
          </FadeIn>
          <h1 className="font-serif font-medium text-ink leading-[1.05] tracking-tight">
            <MaskedLine delay={0.1} className="text-5xl md:text-7xl">
              Pensieri in <span className="italic text-gold">viaggio</span>
            </MaskedLine>
          </h1>
          <FadeIn delay={0.4}>
            <p className="mt-7 max-w-2xl text-base md:text-lg leading-relaxed text-stone">
              Scrivo di viaggi, del mio percorso e di quello che imparo strada
              facendo. Senza filtri, come lo racconterei a un'amica.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="pb-24 md:pb-36">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <p className="text-stone" data-testid="blog-loading">Caricamento articoli…</p>
          ) : posts.length === 0 ? (
            <p className="text-stone" data-testid="blog-empty">
              Gli articoli stanno arrivando. Torna presto a trovarmi.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">
              {posts.map((p, i) => (
                <Reveal key={p.slug} delay={i * 0.1}>
                  <Link
                    to={`/blog/${p.slug}`}
                    data-testid={`blog-card-${p.slug}`}
                    className="group block"
                  >
                    <div className="overflow-hidden rounded-xl">
                      <img
                        src={p.cover_image}
                        alt={p.cover_alt}
                        style={{ objectPosition: p.cover_position || "center" }}
                        className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                        loading="lazy"
                      />
                    </div>
                    <div className="mt-6 flex items-center gap-3 text-xs uppercase tracking-[0.18em]">
                      <span className="text-gold font-semibold">{p.category}</span>
                    </div>
                    <h2 className="mt-3 font-serif text-2xl md:text-[1.7rem] leading-snug text-ink group-hover:text-gold transition-colors duration-300">
                      {p.title}
                    </h2>
                    <p className="mt-3 text-sm md:text-base leading-relaxed text-stone line-clamp-3">
                      {p.excerpt}
                    </p>
                    <span className="mt-4 inline-block text-sm font-semibold text-gold link-underline">
                      Leggi l'articolo
                    </span>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
